import { Language } from '../../../../../../services/api/api-client.types.ts';

type Constructor<T = {}> = new (...args: any[]) => T;
import { LexicalNode } from 'lexical';

export interface SerializedLocalizable {
  id: string;
  originalLanguage: Language | null;
  translations: Record<string, string | null>;
  languagesTranslatedByAI: string[];
  languagesLastManuallyChanged: string[];
  pageDesignerLanguage: Language | null;
}

export const defaultTranslations = {
  [Language.De]: null,
  [Language.En]: null,
  [Language.Fr]: null,
  [Language.Es]: null,
};

export function LocalizableMixin<TBase extends Constructor<LexicalNode>>(
  Base: TBase,
) {
  return class extends Base {
    __id: string = crypto.randomUUID();
    __originalLanguage: Language | null = null;
    __translations: Record<string, string | null> = defaultTranslations;
    __languagesTranslatedByAI: string[] = [];
    __languagesLastManuallyChanged: string[] = [];
    __pageDesignerLanguage: Language | null = null;

    setTranslations(language: Language, text: string) {
      const writable = this.getWritable();
      writable.__translations = {
        ...writable.__translations,
        [language]: text,
      };
      writable.markDirty();
    }
    getTranslation(language: Language) {
      return (
        this.__translations?.[language] ??
        this.__translations?.[language.toLowerCase()] ??
        null
      );
    }

    resetLanguagesTranslatedByAI() {
      const writable = this.getWritable();
      if (writable.__languagesTranslatedByAI.length > 0) {
        writable.__languagesTranslatedByAI = [];
        writable.markDirty();
      }
    }

    addLanguageTranslatedByAI(language: string) {
      const writable = this.getWritable();
      writable.__languagesTranslatedByAI.push(language);
      writable.markDirty();
    }

    removeLanguageTranslatedByAI(language: string) {
      const writable = this.getWritable();
      writable.__languagesTranslatedByAI =
        writable.__languagesTranslatedByAI.filter((l) => l !== language);
      writable.markDirty();
    }

    addLanguageManuallyChanged(language: string) {
      const writable = this.getWritable();
      if (!writable.__languagesLastManuallyChanged.includes(language)) {
        writable.__languagesLastManuallyChanged.push(language);
        writable.markDirty();
      }
    }

    removeLanguageManuallyChanged(language: string) {
      const writable = this.getWritable();
      writable.__languagesLastManuallyChanged =
        writable.__languagesLastManuallyChanged.filter((l) => l !== language);
      writable.markDirty();
    }

    setOriginalLanguage(language: Language) {
      const writable = this.getWritable();
      writable.__originalLanguage = language;
      writable.markDirty();
    }

    setPageDesignerLanguage(language: Language) {
      const writable = this.getWritable();
      writable.__pageDesignerLanguage = language;
      writable.markDirty();
    }

    exportLocalizableData() {
      return {
        id: this.__id,
        originalLanguage: this.__originalLanguage,
        translations: this.__translations,
        languagesTranslatedByAI: this.__languagesTranslatedByAI,
        languagesLastManuallyChanged: this.__languagesLastManuallyChanged,
      };
    }
  };
}
